import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/surfaces/Card.jsx.
const cardVariants = cva(
  'flex flex-col overflow-hidden relative font-sans rounded-[var(--radius-card)] transition',
  {
    variants: {
      variant: {
        elevated: 'bg-card shadow-[var(--shadow-sm)] border border-[var(--border-hairline)]',
        outline: 'bg-card border border-border',
        warm: 'bg-[var(--surface-warm)] border border-[var(--border-hairline)]',
      },
      interactive: {
        true: 'group cursor-pointer hover:-translate-y-0.5 hover:shadow-[var(--shadow-lg)] ' +
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)]',
        false: '',
      },
    },
    defaultVariants: { variant: 'elevated', interactive: false },
  },
);

const padMap = { sm: 'p-4', md: 'p-6', lg: 'p-8' } as const;

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  padding?: 'sm' | 'md' | 'lg';
  media?: ReactNode;
  mediaHeight?: number;
  body?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant, interactive, padding = 'md', media, mediaHeight = 200, body = true, className, children, ...rest },
  ref,
) {
  const interactiveProps = interactive ? { tabIndex: 0, role: 'button' } : {};
  return (
    <div ref={ref} className={cn(cardVariants({ variant, interactive }), className)} {...interactiveProps} {...rest}>
      {media && (
        <div
          className={cn(
            'relative overflow-hidden bg-[var(--ollio-navy)] [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:transition-transform [&_img]:duration-[4000ms]',
            interactive && 'group-hover:[&_img]:scale-[1.04]',
          )}
          style={{ height: mediaHeight }}
        >
          {media}
        </div>
      )}
      {body ? <div className={cn('flex flex-col gap-2', padMap[padding])}>{children}</div> : children}
    </div>
  );
});
